import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';

interface PaymentRow {
  id: string;
  user_id: string;
  kind: string;
  provider: string;
  amount_minor: number;
  currency: string;
  status: string;
  paid_at: string | null;
  created_at: string;
}

interface Summary {
  window_days: number;
  totals: {
    succeeded_count: number;
    failed_count: number;
    refunded_count: number;
    revenue_minor: number;
    refunded_minor: number;
  };
  by_provider: Array<{ provider: string; count: number; revenue_minor: number }>;
  by_day: Array<{ day: string; revenue_minor: number }>;
}

export function PaymentsPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<PaymentRow[] | null>(null);

  useEffect(() => {
    api<{ data: Summary }>('/admin/payments/summary')
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null));
    api<{ data: PaymentRow[] }>('/admin/payments?limit=100')
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, []);

  const successRate =
    summary !== null && summary.totals.succeeded_count + summary.totals.failed_count > 0
      ? Math.round(
          (100 * summary.totals.succeeded_count) /
            (summary.totals.succeeded_count + summary.totals.failed_count),
        )
      : null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">Payments</h1>
        <p className="text-sm text-muted-foreground">
          Revenue, refunds, success rate (last 30 days) + recent transactions.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          title="Revenue (30d)"
          value={summary !== null ? formatMinor(summary.totals.revenue_minor, 'NOK') : '—'}
        />
        <Kpi
          title="Refunded (30d)"
          value={summary !== null ? formatMinor(summary.totals.refunded_minor, 'NOK') : '—'}
        />
        <Kpi
          title="Successful txns"
          value={summary !== null ? String(summary.totals.succeeded_count) : '—'}
        />
        <Kpi title="Success rate" value={successRate !== null ? `${successRate}%` : '—'} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Revenue by provider</CardTitle>
          </CardHeader>
          <CardContent>
            {summary === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
            {summary !== null && summary.by_provider.length === 0 && (
              <p className="text-sm text-muted-foreground">No successful payments yet.</p>
            )}
            {summary !== null && summary.by_provider.length > 0 && (
              <ul className="space-y-2 text-sm">
                {summary.by_provider.map((p) => (
                  <li
                    key={p.provider}
                    className="flex items-center justify-between rounded-2xl border border-border p-3"
                  >
                    <span className="font-medium capitalize">{p.provider}</span>
                    <span className="text-xs text-muted-foreground">{p.count} txns</span>
                    <span className="tabular-nums font-bold">
                      {formatMinor(p.revenue_minor, 'NOK')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Daily revenue (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            {summary === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
            {summary !== null && (
              <BarSeries
                data={summary.by_day.map((d) => ({
                  label: d.day.slice(5),
                  value: Number(d.revenue_minor),
                }))}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {rows === null && <div className="h-32 animate-pulse rounded-2xl bg-muted" />}
          {rows?.length === 0 && <p className="text-sm text-muted-foreground">No payments yet.</p>}
          {rows !== null && rows.length > 0 && (
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-2 font-bold">When</th>
                  <th className="py-2 font-bold">Kind</th>
                  <th className="py-2 font-bold">Provider</th>
                  <th className="py-2 font-bold">Amount</th>
                  <th className="py-2 font-bold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="py-2 text-xs text-muted-foreground">
                      {formatDateTime(p.paid_at ?? p.created_at)}
                    </td>
                    <td className="py-2">
                      <Badge variant="secondary">{p.kind}</Badge>
                    </td>
                    <td className="py-2 capitalize">{p.provider}</td>
                    <td className="py-2 tabular-nums">{formatMinor(p.amount_minor, p.currency)}</td>
                    <td className="py-2">
                      <Badge variant={p.status === 'succeeded' ? 'default' : 'secondary'}>
                        {p.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-black">{value}</p>
      </CardContent>
    </Card>
  );
}

function BarSeries({ data }: { data: Array<{ label: string; value: number }> }) {
  const max = data.reduce((m, d) => Math.max(m, d.value), 1);
  return (
    <div
      className="grid grid-cols-30 gap-1"
      style={{ gridTemplateColumns: `repeat(${Math.max(data.length, 1)}, minmax(0, 1fr))` }}
    >
      {data.map((d) => (
        <div
          key={d.label}
          className="flex flex-col items-center gap-1"
          title={`${d.label}: ${d.value}`}
        >
          <div className="h-24 w-full rounded-t bg-muted" style={{ alignSelf: 'flex-end' }}>
            <div
              className="h-full w-full rounded-t bg-primary"
              style={{ height: `${Math.max(2, Math.round((100 * d.value) / max))}%` }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground">{d.label}</span>
        </div>
      ))}
      {data.length === 0 && (
        <p className="col-span-full text-center text-sm text-muted-foreground">No data.</p>
      )}
    </div>
  );
}

function formatMinor(amountMinor: number | string, currency: string): string {
  const minor = typeof amountMinor === 'string' ? Number(amountMinor) : amountMinor;
  const major = minor / 100;
  if (currency === 'NOK' || currency === 'SEK' || currency === 'DKK')
    return `${Math.round(major)} kr`;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(major);
}

function formatDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}
